import { useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import HooksModalV5Key from "app/constants/HooksModalV5Key";
import { createPluralHelperForI18nKey } from "app/pages/sales-intelligence/utils/strings";
import { openHooksModalV5 } from "app/services/ModalService";

import trackingService from "../../services/tracking/trackingService";
import { selectContactsCost } from "../../store/selectors";

type ModalScenario = "revealAll" | "revealPartial" | "exportRevealedOnly" | "cannotExport";

type RevealBeforeExportModalProps = {
    isOpen: boolean;
    unrevealedCount: number;
    revealedCount: number;
    remainingCredits: number;
    onConfirm: () => void;
    onClose: () => void;
};

const getModalScenario = (unrevealedCount: number, revealedCount: number, remainingCredits: number): ModalScenario => {
    if (remainingCredits >= unrevealedCount) {
        return "revealAll";
    }

    if (remainingCredits > 0) {
        return "revealPartial";
    }

    if (revealedCount > 0) {
        return "exportRevealedOnly";
    }

    return "cannotExport";
};

export const RevealBeforeExportModal = ({
    isOpen,
    unrevealedCount,
    revealedCount,
    remainingCredits,
    onConfirm,
    onClose,
}: RevealBeforeExportModalProps) => {
    const { translate } = useTranslate();
    const contactsCost = useSelector(selectContactsCost);
    const scenario = getModalScenario(unrevealedCount, revealedCount, remainingCredits);
    const creditsToRevealAll = unrevealedCount * contactsCost;
    const creditsToRevealPartial = remainingCredits * contactsCost;

    const handleContactUs = () => {
        trackingService.trackContactUsRevealAndExport();
        openHooksModalV5(HooksModalV5Key.RevealContactsPayingUser);
        onClose();
    };

    const handleCancel = () => {
        trackingService.trackCancelRevealAndExport();
        onClose();
    };

    const renderCreditsMessage = () => {
        switch (scenario) {
            case "revealAll":
                return translate(
                    createPluralHelperForI18nKey("si.contacts_search.export.confirm_reveal.credits_info")(
                        creditsToRevealAll,
                    ),
                    { credits: creditsToRevealAll },
                );

            case "revealPartial":
                return translate(
                    createPluralHelperForI18nKey("si.contacts_search.export.confirm_reveal.credits_info.partial")(
                        creditsToRevealPartial,
                    ),
                    { credits: creditsToRevealPartial },
                );

            case "exportRevealedOnly":
                return translate(
                    createPluralHelperForI18nKey("si.contacts_search.export.confirm_reveal.credits_info.no_credits")(
                        revealedCount,
                    ),
                    { revealedCount },
                );

            case "cannotExport":
                return translate("si.contacts_search.export.confirm_reveal.credits_info.no_credits_all_unrevealed", {
                    revealedCount,
                });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleCancel}
            customStyles={{
                content: {
                    width: "400px",
                },
            }}
            shouldCloseOnEsc
        >
            <Modal.Header>
                <Modal.Title>
                    {translate(
                        scenario === "cannotExport"
                            ? "si.contacts_search.export.confirm_reveal.title.no_credits"
                            : "si.contacts_search.export.confirm_reveal.title",
                    )}
                </Modal.Title>
            </Modal.Header>

            <Stack
                gap="20px"
                padding="16px 24px 32px"
            >
                {scenario === "cannotExport" ? null : (
                    <Text
                        fontVariant="body1"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate("si.contacts_search.export.confirm_reveal.message")}
                    </Text>
                )}
                <Text
                    fontVariant="body1"
                    color={colorsPalettes.carbon[400]}
                    dangerouslySetInnerHTML={{
                        __html: renderCreditsMessage(),
                    }}
                />
            </Stack>

            <Modal.Footer>
                <Button
                    onClick={handleCancel}
                    type="flat"
                >
                    {translate("si.common.button.cancel")}
                </Button>
                <Button
                    onClick={scenario === "cannotExport" ? handleContactUs : onConfirm}
                    type={scenario === "cannotExport" ? "trial" : "primary"}
                >
                    {scenario === "cannotExport"
                        ? translate("si.common.contact_us")
                        : translate("si.common.button.continue")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
